import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Col, Form, Row, Card, Accordion } from "react-bootstrap";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import Select from 'react-select';
import makeAnimated from "react-select/animated";
import Header from "../../../layouts/Header";
import axiosInstance, { opsAxiosInstance } from "../../../api/axios";
import LocationPickerModal from "../../../components/LocationPickerModal";

// Same checklist-admin credentials used in the Checklist Operations panel.
// Hardcoded per prior request — note this ships inside the frontend bundle
// and is therefore visible via dev tools, so it's only as safe as the
// route it's gating.
const CHECKLIST_API_KEY = "sYQ8SawD0mdqOtPO18NGs1paiEccyXJpFzsCiz1wXgC";
const CHECKLIST_API_SECRET = "79SvSQbGhcgCK9GdKTN61xTjiJvelIJVkC7pY7ioPaAOryyqbjy4pir2ckssjoCD";
const CHECKLIST_ADMIN_HEADERS = {
    headers: {
        "X-API-Key": CHECKLIST_API_KEY,
        "X-API-Secret": CHECKLIST_API_SECRET,
    },
};

const AddOutlet = () => {
    const navigate = useNavigate();
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const animatedComponents = makeAnimated();

    const accessToken = localStorage.getItem("authToken");
    const [isLoading, setIsLoading] = useState(false);
    const [groups, setGroups] = useState([]);
    const [purchaseData, setPurchaseData] = useState([]);
    const [foodUnit, setFoodUnit] = useState([]);
    const [beverageUnit, setBeverageUnit] = useState([]);
    const [centralUnit, setCentralUnit] = useState([]);
    const [outletImage, setOutletImage] = useState(null);
    const [activeAccordion, setActiveAccordion] = useState('0');

    // Google Maps
    const [showMapModal, setShowMapModal] = useState(false);

    const [inputData, setInputData] = useState({
        name: "",
        phone: "",
        orderAcceptType: "",
        email: "",
        fssai: '',
        address: "",
        pincode: "",
        minimumDeliveryRadius: "",
        city: "",
        state: "",
        country: "",
        latitude: "",
        longitude: "",
        isEventOutlet: false,
        openingTime: '',
        closingTime: '',
        gstin: '',
        menuGroup: "",
        type: "",
        foodUnitId: "",
        beverageUnitId: "",
        centralUnitId: "",
        // Franchise fields
        franchiseName: "",
        franchisePhone: "",
        franchiseEmail: "",
        // Payment methods — both optional, both can be enabled together
        acceptRazorpay: false,
        acceptPineLab: false,
        pineLabCredentials: {
            merchantId: "",
            securityToken: "",
            clientId: "",
            storeId: ""
        },
        razorpay: {
            linkedAccountId: "",
            accountStatus: "",
        },
        platForms: [{ name: "", webLink: "" }]
    });

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            border: '1px solid #dee2e6',
            borderRadius: '0.375rem',
            minHeight: '32px',
            fontSize: '0.875rem',
            '&:hover': { borderColor: '#86b7fe' },
        }),
        placeholder: (base) => ({ ...base, color: '#6c757d', fontSize: '0.875rem' }),
        menu: (base) => ({ ...base, fontSize: '0.875rem' }),
    };

    const foodUnitOptions = foodUnit?.map((pu) => ({ label: pu?.name, value: pu?._id }));
    const beverageUnitOptions = beverageUnit?.map((pu) => ({ label: pu?.name, value: pu?._id }));
    const centralUnitOptions = centralUnit?.map((pu) => ({ label: pu?.name, value: pu?._id }));

    const isFranchise = inputData.type === "FRANCHISE";

    const accountStatusOptions = [
        { value: 'CREATED', label: 'Created' },
        { value: 'ACTIVATED', label: 'Activated' },
        { value: 'NEEDS_CLARIFICATION', label: 'Needs Clarification' },
        { value: 'REJECTED', label: 'Rejected' },
        { value: 'UNDER_REVIEW', label: 'Under Review' },
    ];

    const getMenuGroup = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("menu-group", { headers });
            if (response?.data?.status === true) {
                setGroups(response?.data?.data?.menuGroup);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                toast.error(error.response.data.error);
            }
        }
    };

    const getProcurementUnits = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get(`procurement-units/list?page=1&limit=500`, { headers });
            setPurchaseData(response?.data?.data?.procurementUnits);
            setFoodUnit(response?.data?.data?.procurementUnits.filter((fu) => fu?.type === 'FOOD'));
            setBeverageUnit(response?.data?.data?.procurementUnits.filter((fu) => fu?.type === 'BEVERAGE'));
            setCentralUnit(response?.data?.data?.procurementUnits.filter((fu) => fu?.type === 'CENTRAL'));
        } catch (error) {
            console.log("Error in fetching purchase unit list :", error);
        }
    };

    const getPostalCodeDetails = async () => {
        if (!inputData.pincode || inputData.pincode.length < 6) return;
        try {
            const response = await axiosInstance.get(`https://api.postalpincode.in/pincode/${inputData.pincode}`);
            if (response?.data[0]?.Status === "Success") {
                const postOffice = response?.data[0]?.PostOffice[0];
                setInputData((prevData) => ({
                    ...prevData,
                    city: postOffice?.Division || "",
                    state: postOffice?.State || "",
                    country: postOffice?.Country || "",
                }));
                toast.success("Location details auto-filled!");
            } else if (response?.data[0]?.Status === "Error") {
                toast.warning("Invalid Pincode. Please enter manually.");
            }
        } catch (error) {
            // Silent fail
        }
    };

    const handleChange = (e, index) => {
        const { name, value, type, checked } = e.target;

        if (index !== undefined) {
            setInputData((prevState) => {
                const updatedPlatforms = [...prevState.platForms];
                updatedPlatforms[index] = { ...updatedPlatforms[index], [name]: value };
                return { ...prevState, platForms: updatedPlatforms };
            });
        } else {
            if (name.includes(".")) {
                const [parentKey, childKey] = name.split(".");
                setInputData((prevState) => ({
                    ...prevState,
                    [parentKey]: { ...prevState[parentKey], [childKey]: value },
                }));
            } else {
                setInputData((prevState) => ({
                    ...prevState,
                    [name]: type === 'checkbox' ? checked : value,
                    // Clear franchise fields when type changes away from FRANCHISE
                    ...(name === "type" && value !== "FRANCHISE"
                        ? { franchiseName: "", franchisePhone: "", franchiseEmail: "" }
                        : {}),
                    ...(name === "pincode" && value === ""
                        ? { city: "", state: "", country: "" }
                        : {}),
                    // Clear the respective credentials when a payment method is disabled
                    ...(name === "acceptRazorpay" && checked === false
                        ? { razorpay: { linkedAccountId: "", accountStatus: "" } }
                        : {}),
                    ...(name === "acceptPineLab" && checked === false
                        ? { pineLabCredentials: { merchantId: "", securityToken: "", clientId: "", storeId: "" } }
                        : {}),
                }));
            }
        }
    };

    const handleUnitChange = (fieldName, selectedOption) => {
        setInputData(prev => ({ ...prev, [fieldName]: selectedOption ? selectedOption.value : '' }));
    };

    const handleImageChange = (e) => {
        const input = document.getElementById("imageInput");
        const file = input?.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const image = new Image();
                image.src = reader.result;
                image.onload = () => {
                    if (image.width === 1410 && image.height === 1100) {
                        setOutletImage(file);
                        toast.success("Image uploaded successfully!");
                    } else {
                        toast.error(`Image must be 1410x1100 pixels`);
                        input.value = "";
                    }
                };
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLocationConfirm = ({ lat, lng }) => {
        setInputData((prev) => ({
            ...prev,
            latitude: lat.toFixed(6),
            longitude: lng.toFixed(6),
        }));
    };

    const handleAddItem = () => {
        setInputData((prevData) => ({
            ...prevData,
            platForms: [...prevData.platForms, { name: "", webLink: "" }],
        }));
    };

    const handleRemoveItem = (index) => {
        setInputData((prevData) => {
            const updatedItems = [...prevData.platForms];
            updatedItems.splice(index, 1);
            return { ...prevData, platForms: updatedItems };
        });
    };

    // Creates the same outlet in the checklist admin system so it's
    // immediately available there for staff, categories, and assignments.
    // Kept separate from addOutlet so a failure here never blocks or rolls
    // back the primary outlet that was just created successfully.
    const syncOutletToChecklistAdmin = async () => {
        const payload = {
            outlet_name: inputData.name,
            address: inputData.address || undefined,
            city: inputData.city || undefined,
            state: inputData.state || undefined,
            latitude: inputData.latitude ? Number(inputData.latitude) : undefined,
            longitude: inputData.longitude ? Number(inputData.longitude) : undefined,
            is_active: 1,
        };

        await opsAxiosInstance.post("/api/admin/outlets", payload, CHECKLIST_ADMIN_HEADERS);
    };

    const addOutlet = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const formData = new FormData();

            formData.append("name", inputData.name);
            formData.append("phone", inputData.phone);
            formData.append("email", inputData.email);
            formData.append("type", inputData.type);
            formData.append("address", inputData.address);
            formData.append("pincode", inputData.pincode);
            formData.append("city", inputData.city);
            formData.append("state", inputData.state);
            formData.append("country", inputData.country);
            formData.append("coordinates[latitude]", inputData.latitude);
            formData.append("coordinates[longitude]", inputData.longitude);
            formData.append("orderAcceptType", inputData.orderAcceptType);
            formData.append("menuGroup", inputData.menuGroup);
            formData.append("openingTime", inputData.openingTime || null);
            formData.append("closingTime", inputData.closingTime || null);
            formData.append("isEventOutlet", inputData.isEventOutlet || false);
            if (inputData.minimumDeliveryRadius) {
                formData.append("minimumDeliveryRadius", inputData.minimumDeliveryRadius);
            }
            formData.append("gstin", inputData.gstin);
            if  (inputData.fssai) {
                formData.append("fssai", inputData.fssai);
            }
            console.log("Food Unit Id :", inputData?.foodUnitId);
            if (inputData.foodUnitId) formData.append("foodUnitId", inputData.foodUnitId);
            if (inputData.beverageUnitId) formData.append("beverageUnitId", inputData.beverageUnitId);
            if (inputData.centralUnitId) formData.append("centralUnitId", inputData.centralUnitId);

            // Payment methods — both optional, both can be sent together
            if (inputData.acceptRazorpay && inputData.acceptPineLab) {
                // Both payment methods enabled
                formData.append("paymentType", "BOTH");
                if (inputData.razorpay.linkedAccountId) {
                    formData.append("razorpay[linkedAccountId]", inputData.razorpay.linkedAccountId);
                }
                if (inputData.razorpay.accountStatus) {
                    formData.append("razorpay[accountStatus]", inputData.razorpay.accountStatus);
                }
                if (inputData.pineLabCredentials.merchantId) {
                    formData.append("pineLabCredentials[merchantId]", inputData.pineLabCredentials.merchantId);
                }
                if (inputData.pineLabCredentials.securityToken) {
                    formData.append("pineLabCredentials[securityToken]", inputData.pineLabCredentials.securityToken);
                }
                if (inputData.pineLabCredentials.clientId) {
                    formData.append("pineLabCredentials[clientId]", inputData.pineLabCredentials.clientId);
                }
                if (inputData.pineLabCredentials.storeId) {
                    formData.append("pineLabCredentials[storeId]", inputData.pineLabCredentials.storeId);
                }
            } else {
                // Only one payment method enabled
                if (inputData.acceptRazorpay) {
                    formData.append("paymentType", "PAYMENT_GATEWAY");
                    if (inputData.razorpay.linkedAccountId) {
                        formData.append("razorpay[linkedAccountId]", inputData.razorpay.linkedAccountId);
                    }
                    if (inputData.razorpay.accountStatus) {
                        formData.append("razorpay[accountStatus]", inputData.razorpay.accountStatus);
                    }
                }
                if (inputData.acceptPineLab) {
                    formData.append("paymentType", "CARD_SWIPE");
                    if (inputData.pineLabCredentials.merchantId) {
                        formData.append("pineLabCredentials[merchantId]", inputData.pineLabCredentials.merchantId);
                    }
                    if (inputData.pineLabCredentials.securityToken) {
                        formData.append("pineLabCredentials[securityToken]", inputData.pineLabCredentials.securityToken);
                    }
                    if (inputData.pineLabCredentials.clientId) {
                        formData.append("pineLabCredentials[clientId]", inputData.pineLabCredentials.clientId);
                    }
                    if (inputData.pineLabCredentials.storeId) {
                        formData.append("pineLabCredentials[storeId]", inputData.pineLabCredentials.storeId);
                    }
                }
            }

            formData.append("outletImage", outletImage);

            // Append franchise fields only if type is FRANCHISE
            if (isFranchise) {
                if (inputData.franchiseName) formData.append("franchiseName", inputData.franchiseName);
                if (inputData.franchisePhone) formData.append("franchisePhone", inputData.franchisePhone);
                if (inputData.franchiseEmail) formData.append("franchiseEmail", inputData.franchiseEmail);
            }

            inputData.platForms.forEach((platform, index) => {
                if (platform.name && platform.webLink) {
                    formData.append(`platForms[${index}][name]`, platform.name);
                    formData.append(`platForms[${index}][webLink]`, platform.webLink);
                }
            });

            const headers = {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${accessToken}`,
            };

            // Call 1: create the outlet in the primary system.
            const response = await axiosInstance.post("outlet", formData, { headers });
            if (response?.data?.status === true) {
                toast.success("Outlet created successfully!");

                // Call 2: mirror the outlet into the checklist admin system.
                // A failure here is surfaced as a warning rather than an
                // error, since the primary outlet already exists at this
                // point and the user shouldn't think the whole thing failed.
                try {
                    await syncOutletToChecklistAdmin();
                } catch (checklistError) {
                    console.error("Failed to sync outlet to checklist admin:", checklistError);
                    toast.warning("Outlet created, but it could not be added to the checklist admin panel. Add it there manually if needed.");
                }

                navigate('/outlet-management/outlets');
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.error?.message ||
                error?.response?.data?.error ||
                "Failed to create outlet";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const isFormValid = () => {
        const base =
            inputData.name &&
            inputData.phone &&
            inputData.email &&
            inputData.address &&
            inputData.pincode &&
            inputData.city &&
            inputData.state &&
            inputData.country &&
            inputData.latitude &&
            inputData.longitude &&
            inputData.menuGroup &&
            inputData.type &&
            inputData.orderAcceptType &&
            inputData.gstin &&
            outletImage;

        // Payment methods are completely optional - no validation required for their fields
        // Even if toggled on, the credential fields are optional
        return Boolean(base);
    };

    useEffect(() => {
        getMenuGroup();
        getProcurementUnits();
    }, []);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid px-0">
                    {/* Header Card */}
                    <Card className="shadow-sm border-0 mb-3 bg-white">
                        <Card.Body className="py-3">
                            <Row className="align-items-center">
                                <Col>
                                    <h5 className="fw-bold mb-0">
                                        <i className="ri-store-3-line me-2 text-primary"></i>
                                        ADD NEW OUTLET
                                    </h5>
                                    <p className="text-muted small mb-0 mt-1">Fill in the details below to create a new outlet</p>
                                </Col>
                                <Col className="text-end">
                                    <Button variant="outline-secondary" size="sm" onClick={() => navigate('/outlet-management/outlets')}>
                                        <i className="ri-arrow-left-line me-1"></i>Back
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Progress Steps */}
                    <Row className="mb-3 g-2">
                        {['Basic', 'Location', 'Business', 'Payment'].map((step, idx) => (
                            <Col key={idx}>
                                <div
                                    className={`text-center shadow-sm fw-semibold p-2 rounded ${parseInt(activeAccordion) === idx ? 'bg-primary text-white' : 'bg-white'}`}
                                    style={{ fontSize: '0.8rem' }}
                                >
                                    {step}
                                </div>
                            </Col>
                        ))}
                    </Row>

                    <Form onSubmit={addOutlet}>
                        <Accordion activeKey={activeAccordion} onSelect={(key) => setActiveAccordion(key)} alwaysOpen={false}>

                            {/* Section 1: Basic Information */}
                            <Accordion.Item eventKey="0" className="border-0 mb-3 shadow-sm">
                                <Accordion.Header className="bg-white">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                                            <i className="ri-information-line text-primary"></i>
                                        </div>
                                        <div>
                                            <h6 className="mb-0 fw-bold">Basic Information</h6>
                                            <small className="text-muted">Outlet name, contact, and type</small>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="pt-3">
                                    <Row className="g-2">
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Outlet Name <span className="text-danger">*</span></Form.Label>
                                                <Form.Control size="sm" type="text" placeholder="e.g., Downtown Cafe" name="name" value={inputData.name} onChange={handleChange} required />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Email <span className="text-danger">*</span></Form.Label>
                                                <Form.Control size="sm" type="email" placeholder="outlet@example.com" name="email" value={inputData.email} onChange={handleChange} required />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Phone <span className="text-danger">*</span></Form.Label>
                                                <Form.Control size="sm" type="tel" placeholder="+91 98765 43210" name="phone" value={inputData.phone} onChange={handleChange} required />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Outlet Type <span className="text-danger">*</span></Form.Label>
                                                <Form.Select size="sm" name="type" value={inputData.type} onChange={handleChange} required>
                                                    <option value="">Select type</option>
                                                    <option value="COMPANY_OWNED">Company Owned</option>
                                                    <option value="FRANCHISE">Franchise</option>
                                                    <option value="EVENT">Event</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Menu Group <span className="text-danger">*</span></Form.Label>
                                                <Form.Select size="sm" name="menuGroup" value={inputData.menuGroup} onChange={handleChange} required>
                                                    <option value="">Select menu</option>
                                                    {groups.map(group => (
                                                        <option key={group._id} value={group._id}>{group.groupName}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Order Type <span className="text-danger">*</span></Form.Label>
                                                <Form.Select size="sm" name="orderAcceptType" value={inputData.orderAcceptType} onChange={handleChange} required>
                                                    <option value="">Select</option>
                                                    <option value="ALL">All Orders</option>
                                                    <option value="PICKUP">Pickup Only</option>
                                                    <option value="DELIVERY">Delivery Only</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {/* Franchise Details — shown only when type is FRANCHISE */}
                                    {isFranchise && (
                                        <div className="mt-3">
                                            <Card className="border-0 bg-warning bg-opacity-10">
                                                <Card.Body className="p-3">
                                                    <div className="d-flex align-items-center mb-3">
                                                        <i className="ri-store-2-line text-warning me-2"></i>
                                                        <h6 className="small fw-bold mb-0 text-warning-emphasis">
                                                            Franchise Details
                                                            <span className="text-muted fw-normal ms-2">(optional)</span>
                                                        </h6>
                                                    </div>
                                                    <Row className="g-2">
                                                        <Col md={4}>
                                                            <Form.Group>
                                                                <Form.Label className="small fw-semibold">Franchise Name</Form.Label>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="text"
                                                                    placeholder="e.g., ABC Franchising Co."
                                                                    name="franchiseName"
                                                                    value={inputData.franchiseName}
                                                                    onChange={handleChange}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Form.Group>
                                                                <Form.Label className="small fw-semibold">Franchise Phone</Form.Label>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="tel"
                                                                    placeholder="+91 98765 43210"
                                                                    name="franchisePhone"
                                                                    value={inputData.franchisePhone}
                                                                    onChange={handleChange}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Form.Group>
                                                                <Form.Label className="small fw-semibold">Franchise Email</Form.Label>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="email"
                                                                    placeholder="franchise@example.com"
                                                                    name="franchiseEmail"
                                                                    value={inputData.franchiseEmail}
                                                                    onChange={handleChange}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>

                            {/* Section 2: Location & Operation */}
                            <Accordion.Item eventKey="1" className="border-0 mb-3 shadow-sm">
                                <Accordion.Header>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                                            <i className="ri-map-pin-line text-primary"></i>
                                        </div>
                                        <div>
                                            <h6 className="mb-0 fw-bold">Location & Operation</h6>
                                            <small className="text-muted">Address, coordinates, and timings</small>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="pt-3">
                                    <Row className="g-2 mb-2">
                                        <Col md={8}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Address <span className="text-danger">*</span></Form.Label>
                                                <Form.Control size="sm" as="textarea" rows={2} placeholder="Street address, landmark, etc." name="address" value={inputData.address} onChange={handleChange} required />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Pincode <span className="text-danger">*</span></Form.Label>
                                                <Form.Control size="sm" type="text" placeholder="6-digit pincode" name="pincode" value={inputData.pincode} onChange={handleChange} onBlur={getPostalCodeDetails} maxLength={6} required />
                                                <Form.Text className="text-muted small">Auto-fills city/state</Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="g-2 mb-2">
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">City <span className="text-danger">*</span></Form.Label>
                                                <Form.Control size="sm" type="text" placeholder="City" name="city" value={inputData.city} onChange={handleChange} required />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">State <span className="text-danger">*</span></Form.Label>
                                                <Form.Control size="sm" type="text" placeholder="State" name="state" value={inputData.state} onChange={handleChange} required />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Country <span className="text-danger">*</span></Form.Label>
                                                <Form.Control size="sm" type="text" placeholder="Country" name="country" value={inputData.country} onChange={handleChange} required />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Event Outlet?</Form.Label>
                                                <div className="d-flex align-items-center mt-1">
                                                    <Form.Check type="switch" id="eventOutletSwitch" onChange={handleChange} checked={inputData?.isEventOutlet} name="isEventOutlet" />
                                                    <span className="ms-2 small">{inputData?.isEventOutlet ? 'Yes' : 'No'}</span>
                                                </div>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="g-2 mb-2">
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Latitude <span className="text-danger">*</span></Form.Label>
                                                <Form.Control size="sm" type="number" step="any" placeholder="e.g., 28.6139" name="latitude" value={inputData.latitude} onChange={handleChange} required />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Longitude <span className="text-danger">*</span></Form.Label>
                                                <Form.Control size="sm" type="number" step="any" placeholder="e.g., 77.2090" name="longitude" value={inputData.longitude} onChange={handleChange} required />
                                            </Form.Group>
                                        </Col>
                                        <Col md={2} className="d-flex align-items-end">
                                            <Button variant="outline-primary" size="sm" className="mb-1 w-100" onClick={() => setShowMapModal(true)}>
                                                <i className="ri-map-2-line me-1"></i>Pick on Map
                                            </Button>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Opening Time</Form.Label>
                                                <Form.Control size="sm" type="time" name="openingTime" value={inputData.openingTime} onChange={handleChange} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Closing Time</Form.Label>
                                                <Form.Control size="sm" type="time" name="closingTime" value={inputData.closingTime} onChange={handleChange} />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="g-2">
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Delivery Radius (km)</Form.Label>
                                                <Form.Control size="sm" type="number" min="0" step="0.5" placeholder="e.g., 5" name="minimumDeliveryRadius" value={inputData.minimumDeliveryRadius} onChange={handleChange} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Accordion.Body>
                            </Accordion.Item>

                            {/* Section 3: Business & Legal */}
                            <Accordion.Item eventKey="2" className="border-0 mb-3 shadow-sm">
                                <Accordion.Header>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                                            <i className="ri-briefcase-line text-primary"></i>
                                        </div>
                                        <div>
                                            <h6 className="mb-0 fw-bold">Business & Legal</h6>
                                            <small className="text-muted">GST, FSSAI, units, and image</small>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="pt-3">
                                    <Row className="g-2 mb-3">
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">GSTIN <span className="text-danger">*</span></Form.Label>
                                                <Form.Control size="sm" type="text" placeholder="22AAAAA0000A1Z5" name="gstin" value={inputData.gstin} onChange={handleChange} required />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">FSSAI</Form.Label>
                                                <Form.Control size="sm" type="text" placeholder="FSSAI license number" name="fssai" value={inputData.fssai} onChange={handleChange} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Outlet Image <span className="text-danger">*</span></Form.Label>
                                                <Form.Control id="imageInput" size="sm" accept="image/*" type="file" onChange={handleImageChange} required />
                                                <Form.Text className="text-muted small">1410x1100 pixels recommended</Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="g-2">
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Food Unit</Form.Label>
                                                <Select options={foodUnitOptions} value={foodUnitOptions?.find(o => o.value === inputData.foodUnitId)} onChange={(sel) => handleUnitChange('foodUnitId', sel)} placeholder="Select Food Unit" isSearchable components={animatedComponents} styles={customSelectStyles} isClearable />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Beverage Unit</Form.Label>
                                                <Select options={beverageUnitOptions} value={beverageUnitOptions?.find(o => o.value === inputData.beverageUnitId)} onChange={(sel) => handleUnitChange('beverageUnitId', sel)} placeholder="Select Beverage Unit" isSearchable components={animatedComponents} styles={customSelectStyles} isClearable />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Central Unit</Form.Label>
                                                <Select options={centralUnitOptions} value={centralUnitOptions?.find(o => o.value === inputData.centralUnitId)} onChange={(sel) => handleUnitChange('centralUnitId', sel)} placeholder="Select Central Unit" isSearchable styles={customSelectStyles} components={animatedComponents} isClearable />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Accordion.Body>
                            </Accordion.Item>

                            {/* Section 4: Payment & Platforms */}
                            <Accordion.Item eventKey="3" className="border-0 mb-3 shadow-sm">
                                <Accordion.Header>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                                            <i className="ri-bank-card-line text-primary"></i>
                                        </div>
                                        <div>
                                            <h6 className="mb-0 fw-bold">Payment & Platforms</h6>
                                            <small className="text-muted">Enable one or both payment methods, and delivery platforms</small>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="pt-3">
                                    <Row className="g-2 mb-3">
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold d-block">Accept Razorpay <span className="text-muted fw-normal"></span></Form.Label>
                                                <Form.Check
                                                    type="switch"
                                                    id="acceptRazorpaySwitch"
                                                    name="acceptRazorpay"
                                                    checked={inputData.acceptRazorpay}
                                                    onChange={handleChange}
                                                    label={inputData.acceptRazorpay ? "Enabled" : "Disabled"}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold d-block">Accept Pine Labs Card Swipe <span className="text-muted fw-normal"></span></Form.Label>
                                                <Form.Check
                                                    type="switch"
                                                    id="acceptPineLabSwitch"
                                                    name="acceptPineLab"
                                                    checked={inputData.acceptPineLab}
                                                    onChange={handleChange}
                                                    label={inputData.acceptPineLab ? "Enabled" : "Disabled"}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {inputData.acceptRazorpay && (
                                        <Card className="bg-light border-0 mb-3">
                                            <Card.Body className="p-3">
                                                <h6 className="small fw-bold mb-2">Razorpay Details <span className="text-muted fw-normal">(Optional)</span></h6>
                                                <Row className="g-2">
                                                    <Col md={4}>
                                                        <Form.Group>
                                                            <Form.Label className="small fw-semibold">Linked Account ID</Form.Label>
                                                            <Form.Control
                                                                size="sm" type="text" placeholder="Linked Account ID (acc_XXXXXXXX)"
                                                                name="razorpay.linkedAccountId"
                                                                value={inputData?.razorpay?.linkedAccountId}
                                                                onChange={handleChange}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={4}>
                                                        <Form.Group>
                                                            <Form.Label className="small fw-semibold">Account Status</Form.Label>
                                                            <Form.Select
                                                                size="sm"
                                                                name="razorpay.accountStatus"
                                                                value={inputData?.razorpay?.accountStatus}
                                                                onChange={handleChange}
                                                            >
                                                                <option value="">Select Status</option>
                                                                {accountStatusOptions.map(option => (
                                                                    <option key={option.value} value={option.value}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    )}

                                    {inputData.acceptPineLab && (
                                        <Card className="bg-light border-0 mb-3">
                                            <Card.Body className="p-3">
                                                <h6 className="small fw-bold mb-2">Pine Labs Credentials <span className="text-muted fw-normal">(Optional)</span></h6>
                                                <Row className="g-2">
                                                    <Col md={3}>
                                                        <Form.Control size="sm" type="text" placeholder="Merchant ID" name="pineLabCredentials.merchantId" value={inputData?.pineLabCredentials?.merchantId} onChange={handleChange} />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Control size="sm" type="text" placeholder="Security Token" name="pineLabCredentials.securityToken" value={inputData?.pineLabCredentials?.securityToken} onChange={handleChange} />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Control size="sm" type="text" placeholder="Client ID" name="pineLabCredentials.clientId" value={inputData?.pineLabCredentials?.clientId} onChange={handleChange} />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Control size="sm" type="text" placeholder="Store ID" name="pineLabCredentials.storeId" value={inputData?.pineLabCredentials?.storeId} onChange={handleChange} />
                                                    </Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    )}

                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="small fw-bold mb-0">Delivery Platforms</h6>
                                        <Button size="sm" variant="outline-primary" onClick={handleAddItem}>
                                            <i className="ri-add-line me-1"></i>Add Platform
                                        </Button>
                                    </div>

                                    {inputData?.platForms?.map((item, index) => (
                                        <Card key={index} className="bg-light border-0 mb-2">
                                            <Card.Body className="p-2">
                                                <Row className="g-2 align-items-center">
                                                    <Col md={5}>
                                                        <Form.Control size="sm" type="text" placeholder="Platform name (e.g., Zomato)" name="name" value={item.name} onChange={(e) => handleChange(e, index)} />
                                                    </Col>
                                                    <Col md={5}>
                                                        <Form.Control size="sm" type="text" placeholder="https://example.com" name="webLink" value={item.webLink} onChange={(e) => handleChange(e, index)} />
                                                    </Col>
                                                    <Col md={2}>
                                                        {inputData.platForms.length > 1 && (
                                                            <Button size="sm" variant="outline-danger" onClick={() => handleRemoveItem(index)} className="w-100">
                                                                <i className="ri-delete-bin-line"></i>
                                                            </Button>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>

                        {/* Form Actions */}
                        <Card className="border-0 shadow-sm mt-3">
                            <Card.Body className="py-3">
                                <Row className="align-items-center">
                                    <Col>
                                        <div className="d-flex align-items-center">
                                            <i className="ri-information-line text-primary me-2"></i>
                                            <small className="text-muted">
                                                Fields marked with <span className="text-danger">*</span> are required
                                            </small>
                                        </div>
                                    </Col>
                                    <Col className="text-end">
                                        <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => navigate('/outlet-management/outlets')}>
                                            Cancel
                                        </Button>
                                        <Button type="submit" variant="primary" size="sm" disabled={isLoading || !isFormValid()} className="px-4">
                                            {isLoading ? (
                                                <><span className="spinner-border spinner-border-sm me-2" />Creating...</>
                                            ) : (
                                                'Create Outlet'
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Form>
                </div>
                <LocationPickerModal
                    show={showMapModal}
                    onHide={() => setShowMapModal(false)}
                    onConfirm={handleLocationConfirm}
                    initialLat={inputData.latitude}
                    initialLng={inputData.longitude}
                />
            </div>
        </React.Fragment>
    );
};

export default AddOutlet;



// Old Outlet Code 
// import React, { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { Button, Col, Form, Row, Card, Accordion } from "react-bootstrap";
// import { toast } from "react-toastify";
// import { useSelector } from "react-redux";
// import Select from 'react-select';
// import makeAnimated from "react-select/animated";
// import Header from "../../../layouts/Header";
// import axiosInstance, { opsAxiosInstance } from "../../../api/axios";
// import LocationPickerModal from "../../../components/LocationPickerModal";

// // Same checklist-admin credentials used in the Checklist Operations panel.
// // Hardcoded per prior request — note this ships inside the frontend bundle
// // and is therefore visible via dev tools, so it's only as safe as the
// // route it's gating.
// const CHECKLIST_API_KEY = "sYQ8SawD0mdqOtPO18NGs1paiEccyXJpFzsCiz1wXgC";
// const CHECKLIST_API_SECRET = "79SvSQbGhcgCK9GdKTN61xTjiJvelIJVkC7pY7ioPaAOryyqbjy4pir2ckssjoCD";
// const CHECKLIST_ADMIN_HEADERS = {
//     headers: {
//         "X-API-Key": CHECKLIST_API_KEY,
//         "X-API-Secret": CHECKLIST_API_SECRET,
//     },
// };

// const AddOutlet = () => {
//     const navigate = useNavigate();
//     const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
//     const [skin, setSkin] = useState(currentSkin);
//     const animatedComponents = makeAnimated();

//     const accessToken = localStorage.getItem("authToken");
//     const [isLoading, setIsLoading] = useState(false);
//     const [groups, setGroups] = useState([]);
//     const [purchaseData, setPurchaseData] = useState([]);
//     const [foodUnit, setFoodUnit] = useState([]);
//     const [beverageUnit, setBeverageUnit] = useState([]);
//     const [centralUnit, setCentralUnit] = useState([]);
//     const [outletImage, setOutletImage] = useState(null);
//     const [activeAccordion, setActiveAccordion] = useState('0');

//     // Google Maps
//     const [showMapModal, setShowMapModal] = useState(false);

//     const [inputData, setInputData] = useState({
//         name: "",
//         phone: "",
//         orderAcceptType: "",
//         email: "",
//         fssai: '',
//         address: "",
//         pincode: "",
//         minimumDeliveryRadius: "",
//         city: "",
//         state: "",
//         country: "",
//         latitude: "",
//         longitude: "",
//         isEventOutlet: false,
//         openingTime: '',
//         closingTime: '',
//         gstin: '',
//         paymentType: "",
//         menuGroup: "",
//         type: "",
//         foodUnitId: "",
//         beverageUnitId: "",
//         centralUnitId: "",
//         // Franchise fields
//         franchiseName: "",
//         franchisePhone: "",
//         franchiseEmail: "",
//         pineLabCredentials: {
//             merchantId: "",
//             securityToken: "",
//             clientId: "",
//             storeId: ""
//         },
//         platForms: [{ name: "", webLink: "" }]
//     });

//     const customSelectStyles = {
//         control: (base) => ({
//             ...base,
//             border: '1px solid #dee2e6',
//             borderRadius: '0.375rem',
//             minHeight: '32px',
//             fontSize: '0.875rem',
//             '&:hover': { borderColor: '#86b7fe' },
//         }),
//         placeholder: (base) => ({ ...base, color: '#6c757d', fontSize: '0.875rem' }),
//         menu: (base) => ({ ...base, fontSize: '0.875rem' }),
//     };

//     const foodUnitOptions = foodUnit?.map((pu) => ({ label: pu?.name, value: pu?._id }));
//     const beverageUnitOptions = beverageUnit?.map((pu) => ({ label: pu?.name, value: pu?._id }));
//     const centralUnitOptions = centralUnit?.map((pu) => ({ label: pu?.name, value: pu?._id }));

//     const isFranchise = inputData.type === "FRANCHISE";

//     const getMenuGroup = async () => {
//         try {
//             const headers = { Authorization: `Bearer ${accessToken}` };
//             const response = await axiosInstance.get("menu-group", { headers });
//             if (response?.data?.status === true) {
//                 setGroups(response?.data?.data?.menuGroup);
//             }
//         } catch (error) {
//             if (error.response && error.response?.data?.status === false) {
//                 toast.error(error.response.data.error);
//             }
//         }
//     };

//     const getProcurementUnits = async () => {
//         try {
//             const headers = { Authorization: `Bearer ${accessToken}` };
//             const response = await axiosInstance.get(`procurement-units/list?page=1&limit=500`, { headers });
//             setPurchaseData(response?.data?.data?.procurementUnits);
//             setFoodUnit(response?.data?.data?.procurementUnits.filter((fu) => fu?.type === 'FOOD'));
//             setBeverageUnit(response?.data?.data?.procurementUnits.filter((fu) => fu?.type === 'BEVERAGE'));
//             setCentralUnit(response?.data?.data?.procurementUnits.filter((fu) => fu?.type === 'CENTRAL'));
//         } catch (error) {
//             console.log("Error in fetching purchase unit list :", error);
//         }
//     };

//     const getPostalCodeDetails = async () => {
//         if (!inputData.pincode || inputData.pincode.length < 6) return;
//         try {
//             const response = await axiosInstance.get(`https://api.postalpincode.in/pincode/${inputData.pincode}`);
//             if (response?.data[0]?.Status === "Success") {
//                 const postOffice = response?.data[0]?.PostOffice[0];
//                 setInputData((prevData) => ({
//                     ...prevData,
//                     city: postOffice?.Division || "",
//                     state: postOffice?.State || "",
//                     country: postOffice?.Country || "",
//                 }));
//                 toast.success("Location details auto-filled!");
//             } else if (response?.data[0]?.Status === "Error") {
//                 toast.warning("Invalid Pincode. Please enter manually.");
//             }
//         } catch (error) {
//             // Silent fail
//         }
//     };

//     const handleChange = (e, index) => {
//         const { name, value, type, checked } = e.target;

//         if (index !== undefined) {
//             setInputData((prevState) => {
//                 const updatedPlatforms = [...prevState.platForms];
//                 updatedPlatforms[index] = { ...updatedPlatforms[index], [name]: value };
//                 return { ...prevState, platForms: updatedPlatforms };
//             });
//         } else {
//             if (name.includes(".")) {
//                 const [parentKey, childKey] = name.split(".");
//                 setInputData((prevState) => ({
//                     ...prevState,
//                     [parentKey]: { ...prevState[parentKey], [childKey]: value },
//                 }));
//             } else {
//                 setInputData((prevState) => ({
//                     ...prevState,
//                     [name]: type === 'checkbox' ? checked : value,
//                     // Clear franchise fields when type changes away from FRANCHISE
//                     ...(name === "type" && value !== "FRANCHISE"
//                         ? { franchiseName: "", franchisePhone: "", franchiseEmail: "" }
//                         : {}),
//                     ...(name === "pincode" && value === ""
//                         ? { city: "", state: "", country: "" }
//                         : {}),
//                 }));
//             }
//         }
//     };

//     const handleUnitChange = (fieldName, selectedOption) => {
//         setInputData(prev => ({ ...prev, [fieldName]: selectedOption ? selectedOption.value : '' }));
//     };

//     const handleImageChange = (e) => {
//         const input = document.getElementById("imageInput");
//         const file = input?.files[0];
//         if (file) {
//             const reader = new FileReader();
//             reader.onloadend = () => {
//                 const image = new Image();
//                 image.src = reader.result;
//                 image.onload = () => {
//                     if (image.width === 1410 && image.height === 1100) {
//                         setOutletImage(file);
//                         toast.success("Image uploaded successfully!");
//                     } else {
//                         toast.error(`Image must be 1410x1100 pixels`);
//                         input.value = "";
//                     }
//                 };
//             };
//             reader.readAsDataURL(file);
//         }
//     };

//     const handleLocationConfirm = ({ lat, lng }) => {
//         setInputData((prev) => ({
//             ...prev,
//             latitude: lat.toFixed(6),
//             longitude: lng.toFixed(6),
//         }));
//     };

//     const handleAddItem = () => {
//         setInputData((prevData) => ({
//             ...prevData,
//             platForms: [...prevData.platForms, { name: "", webLink: "" }],
//         }));
//     };

//     const handleRemoveItem = (index) => {
//         setInputData((prevData) => {
//             const updatedItems = [...prevData.platForms];
//             updatedItems.splice(index, 1);
//             return { ...prevData, platForms: updatedItems };
//         });
//     };

//     // Creates the same outlet in the checklist admin system so it's
//     // immediately available there for staff, categories, and assignments.
//     // Kept separate from addOutlet so a failure here never blocks or rolls
//     // back the primary outlet that was just created successfully.
//     const syncOutletToChecklistAdmin = async () => {
//         const payload = {
//             outlet_name: inputData.name,
//             address: inputData.address || undefined,
//             city: inputData.city || undefined,
//             state: inputData.state || undefined,
//             latitude: inputData.latitude ? Number(inputData.latitude) : undefined,
//             longitude: inputData.longitude ? Number(inputData.longitude) : undefined,
//             is_active: 1,
//         };

//         await opsAxiosInstance.post("/api/admin/outlets", payload, CHECKLIST_ADMIN_HEADERS);
//     };

//     const addOutlet = async (e) => {
//         e.preventDefault();
//         setIsLoading(true);
//         try {
//             const formData = new FormData();

//             formData.append("name", inputData.name);
//             formData.append("phone", inputData.phone);
//             formData.append("email", inputData.email);
//             formData.append("type", inputData.type);
//             formData.append("address", inputData.address);
//             formData.append("pincode", inputData.pincode);
//             formData.append("city", inputData.city);
//             formData.append("state", inputData.state);
//             formData.append("country", inputData.country);
//             formData.append("coordinates[latitude]", inputData.latitude);
//             formData.append("coordinates[longitude]", inputData.longitude);
//             formData.append("orderAcceptType", inputData.orderAcceptType);
//             formData.append("menuGroup", inputData.menuGroup);
//             formData.append("openingTime", inputData.openingTime || null);
//             formData.append("closingTime", inputData.closingTime || null);
//             formData.append("isEventOutlet", inputData.isEventOutlet || false);
//             if (inputData.minimumDeliveryRadius) {
//                 formData.append("minimumDeliveryRadius", inputData.minimumDeliveryRadius);
//             }
//             formData.append("gstin", inputData.gstin);
//             formData.append("fssai", inputData.fssai);
//             console.log("Food Unit Id :", inputData?.foodUnitId);
//             if (inputData.foodUnitId) formData.append("foodUnitId", inputData.foodUnitId);
//             if (inputData.beverageUnitId) formData.append("beverageUnitId", inputData.beverageUnitId);
//             if (inputData.centralUnitId) formData.append("centralUnitId", inputData.centralUnitId);
//             formData.append("paymentType", inputData.paymentType);
//             if (inputData.paymentType === "CARD_SWIPE") {
//                 formData.append("pineLabCredentials[merchantId]", inputData.pineLabCredentials.merchantId);
//                 formData.append("pineLabCredentials[securityToken]", inputData.pineLabCredentials.securityToken);
//                 formData.append("pineLabCredentials[clientId]", inputData.pineLabCredentials.clientId);
//                 formData.append("pineLabCredentials[storeId]", inputData.pineLabCredentials.storeId);
//             }
//             formData.append("outletImage", outletImage);

//             // Append franchise fields only if type is FRANCHISE
//             if (isFranchise) {
//                 if (inputData.franchiseName) formData.append("franchiseName", inputData.franchiseName);
//                 if (inputData.franchisePhone) formData.append("franchisePhone", inputData.franchisePhone);
//                 if (inputData.franchiseEmail) formData.append("franchiseEmail", inputData.franchiseEmail);
//             }

//             inputData.platForms.forEach((platform, index) => {
//                 if (platform.name && platform.webLink) {
//                     formData.append(`platForms[${index}][name]`, platform.name);
//                     formData.append(`platForms[${index}][webLink]`, platform.webLink);
//                 }
//             });

//             const headers = {
//                 "Content-Type": "multipart/form-data",
//                 Authorization: `Bearer ${accessToken}`,
//             };

//             // Call 1: create the outlet in the primary system.
//             const response = await axiosInstance.post("outlet", formData, { headers });
//             if (response?.data?.status === true) {
//                 toast.success("Outlet created successfully!");

//                 // Call 2: mirror the outlet into the checklist admin system.
//                 // A failure here is surfaced as a warning rather than an
//                 // error, since the primary outlet already exists at this
//                 // point and the user shouldn't think the whole thing failed.
//                 try {
//                     await syncOutletToChecklistAdmin();
//                 } catch (checklistError) {
//                     console.error("Failed to sync outlet to checklist admin:", checklistError);
//                     toast.warning("Outlet created, but it could not be added to the checklist admin panel. Add it there manually if needed.");
//                 }

//                 navigate('/outlet-management/outlets');
//             }
//         } catch (error) {
//             const errorMessage = error?.response?.data?.error?.message ||
//                 error?.response?.data?.error ||
//                 "Failed to create outlet";
//             toast.error(errorMessage);
//         } finally {
//             setIsLoading(false);
//         }
//     };

//     const isFormValid = () => {
//         return (
//             inputData.name &&
//             inputData.phone &&
//             inputData.email &&
//             inputData.address &&
//             inputData.pincode &&
//             inputData.city &&
//             inputData.state &&
//             inputData.country &&
//             inputData.latitude &&
//             inputData.longitude &&
//             inputData.menuGroup &&
//             inputData.type &&
//             inputData.orderAcceptType &&
//             inputData.gstin &&
//             inputData.paymentType &&
//             outletImage
//         );
//     };

//     useEffect(() => {
//         getMenuGroup();
//         getProcurementUnits();
//     }, []);

//     useEffect(() => {
//         if (inputData.paymentType === "PAYMENT_GATEWAY") {
//             setInputData((prev) => ({
//                 ...prev,
//                 pineLabCredentials: { merchantId: "", clientId: "", securityToken: "", storeId: "" }
//             }));
//         }
//     }, [inputData.paymentType]);

//     return (
//         <React.Fragment>
//             <Header onSkin={setSkin} />
//             <div className="main main-app p-3 p-lg-4">
//                 <div className="container-fluid px-0">
//                     {/* Header Card */}
//                     <Card className="shadow-sm border-0 mb-3 bg-white">
//                         <Card.Body className="py-3">
//                             <Row className="align-items-center">
//                                 <Col>
//                                     <h5 className="fw-bold mb-0">
//                                         <i className="ri-store-3-line me-2 text-primary"></i>
//                                         ADD NEW OUTLET
//                                     </h5>
//                                     <p className="text-muted small mb-0 mt-1">Fill in the details below to create a new outlet</p>
//                                 </Col>
//                                 <Col className="text-end">
//                                     <Button variant="outline-secondary" size="sm" onClick={() => navigate('/outlet-management/outlets')}>
//                                         <i className="ri-arrow-left-line me-1"></i>Back
//                                     </Button>
//                                 </Col>
//                             </Row>
//                         </Card.Body>
//                     </Card>

//                     {/* Progress Steps */}
//                     <Row className="mb-3 g-2">
//                         {['Basic', 'Location', 'Business', 'Payment'].map((step, idx) => (
//                             <Col key={idx}>
//                                 <div
//                                     className={`text-center shadow-sm fw-semibold p-2 rounded ${parseInt(activeAccordion) === idx ? 'bg-primary text-white' : 'bg-white'}`}
//                                     style={{ fontSize: '0.8rem' }}
//                                 >
//                                     {step}
//                                 </div>
//                             </Col>
//                         ))}
//                     </Row>

//                     <Form onSubmit={addOutlet}>
//                         <Accordion activeKey={activeAccordion} onSelect={(key) => setActiveAccordion(key)} alwaysOpen={false}>

//                             {/* Section 1: Basic Information */}
//                             <Accordion.Item eventKey="0" className="border-0 mb-3 shadow-sm">
//                                 <Accordion.Header className="bg-white">
//                                     <div className="d-flex align-items-center">
//                                         <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
//                                             <i className="ri-information-line text-primary"></i>
//                                         </div>
//                                         <div>
//                                             <h6 className="mb-0 fw-bold">Basic Information</h6>
//                                             <small className="text-muted">Outlet name, contact, and type</small>
//                                         </div>
//                                     </div>
//                                 </Accordion.Header>
//                                 <Accordion.Body className="pt-3">
//                                     <Row className="g-2">
//                                         <Col md={4}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Outlet Name <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Control size="sm" type="text" placeholder="e.g., Downtown Cafe" name="name" value={inputData.name} onChange={handleChange} required />
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={4}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Email <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Control size="sm" type="email" placeholder="outlet@example.com" name="email" value={inputData.email} onChange={handleChange} required />
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={4}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Phone <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Control size="sm" type="tel" placeholder="+91 98765 43210" name="phone" value={inputData.phone} onChange={handleChange} required />
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={4}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Outlet Type <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Select size="sm" name="type" value={inputData.type} onChange={handleChange} required>
//                                                     <option value="">Select type</option>
//                                                     <option value="COMPANY_OWNED">Company Owned</option>
//                                                     <option value="FRANCHISE">Franchise</option>
//                                                     <option value="EVENT">Event</option>
//                                                 </Form.Select>
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={4}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Menu Group <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Select size="sm" name="menuGroup" value={inputData.menuGroup} onChange={handleChange} required>
//                                                     <option value="">Select menu</option>
//                                                     {groups.map(group => (
//                                                         <option key={group._id} value={group._id}>{group.groupName}</option>
//                                                     ))}
//                                                 </Form.Select>
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={4}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Order Type <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Select size="sm" name="orderAcceptType" value={inputData.orderAcceptType} onChange={handleChange} required>
//                                                     <option value="">Select</option>
//                                                     <option value="ALL">All Orders</option>
//                                                     <option value="PICKUP">Pickup Only</option>
//                                                     <option value="DELIVERY">Delivery Only</option>
//                                                 </Form.Select>
//                                             </Form.Group>
//                                         </Col>
//                                     </Row>

//                                     {/* Franchise Details — shown only when type is FRANCHISE */}
//                                     {isFranchise && (
//                                         <div className="mt-3">
//                                             <Card className="border-0 bg-warning bg-opacity-10">
//                                                 <Card.Body className="p-3">
//                                                     <div className="d-flex align-items-center mb-3">
//                                                         <i className="ri-store-2-line text-warning me-2"></i>
//                                                         <h6 className="small fw-bold mb-0 text-warning-emphasis">
//                                                             Franchise Details
//                                                             <span className="text-muted fw-normal ms-2">(optional)</span>
//                                                         </h6>
//                                                     </div>
//                                                     <Row className="g-2">
//                                                         <Col md={4}>
//                                                             <Form.Group>
//                                                                 <Form.Label className="small fw-semibold">Franchise Name</Form.Label>
//                                                                 <Form.Control
//                                                                     size="sm"
//                                                                     type="text"
//                                                                     placeholder="e.g., ABC Franchising Co."
//                                                                     name="franchiseName"
//                                                                     value={inputData.franchiseName}
//                                                                     onChange={handleChange}
//                                                                 />
//                                                             </Form.Group>
//                                                         </Col>
//                                                         <Col md={4}>
//                                                             <Form.Group>
//                                                                 <Form.Label className="small fw-semibold">Franchise Phone</Form.Label>
//                                                                 <Form.Control
//                                                                     size="sm"
//                                                                     type="tel"
//                                                                     placeholder="+91 98765 43210"
//                                                                     name="franchisePhone"
//                                                                     value={inputData.franchisePhone}
//                                                                     onChange={handleChange}
//                                                                 />
//                                                             </Form.Group>
//                                                         </Col>
//                                                         <Col md={4}>
//                                                             <Form.Group>
//                                                                 <Form.Label className="small fw-semibold">Franchise Email</Form.Label>
//                                                                 <Form.Control
//                                                                     size="sm"
//                                                                     type="email"
//                                                                     placeholder="franchise@example.com"
//                                                                     name="franchiseEmail"
//                                                                     value={inputData.franchiseEmail}
//                                                                     onChange={handleChange}
//                                                                 />
//                                                             </Form.Group>
//                                                         </Col>
//                                                     </Row>
//                                                 </Card.Body>
//                                             </Card>
//                                         </div>
//                                     )}
//                                 </Accordion.Body>
//                             </Accordion.Item>

//                             {/* Section 2: Location & Operation */}
//                             <Accordion.Item eventKey="1" className="border-0 mb-3 shadow-sm">
//                                 <Accordion.Header>
//                                     <div className="d-flex align-items-center">
//                                         <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
//                                             <i className="ri-map-pin-line text-primary"></i>
//                                         </div>
//                                         <div>
//                                             <h6 className="mb-0 fw-bold">Location & Operation</h6>
//                                             <small className="text-muted">Address, coordinates, and timings</small>
//                                         </div>
//                                     </div>
//                                 </Accordion.Header>
//                                 <Accordion.Body className="pt-3">
//                                     <Row className="g-2 mb-2">
//                                         <Col md={8}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Address <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Control size="sm" as="textarea" rows={2} placeholder="Street address, landmark, etc." name="address" value={inputData.address} onChange={handleChange} required />
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={4}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Pincode <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Control size="sm" type="text" placeholder="6-digit pincode" name="pincode" value={inputData.pincode} onChange={handleChange} onBlur={getPostalCodeDetails} maxLength={6} required />
//                                                 <Form.Text className="text-muted small">Auto-fills city/state</Form.Text>
//                                             </Form.Group>
//                                         </Col>
//                                     </Row>

//                                     <Row className="g-2 mb-2">
//                                         <Col md={3}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">City <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Control size="sm" type="text" placeholder="City" name="city" value={inputData.city} onChange={handleChange} required />
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={3}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">State <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Control size="sm" type="text" placeholder="State" name="state" value={inputData.state} onChange={handleChange} required />
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={3}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Country <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Control size="sm" type="text" placeholder="Country" name="country" value={inputData.country} onChange={handleChange} required />
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={3}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Event Outlet?</Form.Label>
//                                                 <div className="d-flex align-items-center mt-1">
//                                                     <Form.Check type="switch" id="eventOutletSwitch" onChange={handleChange} checked={inputData?.isEventOutlet} name="isEventOutlet" />
//                                                     <span className="ms-2 small">{inputData?.isEventOutlet ? 'Yes' : 'No'}</span>
//                                                 </div>
//                                             </Form.Group>
//                                         </Col>
//                                     </Row>

//                                     <Row className="g-2 mb-2">
//                                         <Col md={3}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Latitude <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Control size="sm" type="number" step="any" placeholder="e.g., 28.6139" name="latitude" value={inputData.latitude} onChange={handleChange} required />
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={3}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Longitude <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Control size="sm" type="number" step="any" placeholder="e.g., 77.2090" name="longitude" value={inputData.longitude} onChange={handleChange} required />
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={2} className="d-flex align-items-end">
//                                             <Button variant="outline-primary" size="sm" className="mb-1 w-100" onClick={() => setShowMapModal(true)}>
//                                                 <i className="ri-map-2-line me-1"></i>Pick on Map
//                                             </Button>
//                                         </Col>
//                                         <Col md={3}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Opening Time</Form.Label>
//                                                 <Form.Control size="sm" type="time" name="openingTime" value={inputData.openingTime} onChange={handleChange} />
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={3}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Closing Time</Form.Label>
//                                                 <Form.Control size="sm" type="time" name="closingTime" value={inputData.closingTime} onChange={handleChange} />
//                                             </Form.Group>
//                                         </Col>
//                                     </Row>

//                                     <Row className="g-2">
//                                         <Col md={4}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Delivery Radius (km)</Form.Label>
//                                                 <Form.Control size="sm" type="number" min="0" step="0.5" placeholder="e.g., 5" name="minimumDeliveryRadius" value={inputData.minimumDeliveryRadius} onChange={handleChange} />
//                                             </Form.Group>
//                                         </Col>
//                                     </Row>
//                                 </Accordion.Body>
//                             </Accordion.Item>

//                             {/* Section 3: Business & Legal */}
//                             <Accordion.Item eventKey="2" className="border-0 mb-3 shadow-sm">
//                                 <Accordion.Header>
//                                     <div className="d-flex align-items-center">
//                                         <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
//                                             <i className="ri-briefcase-line text-primary"></i>
//                                         </div>
//                                         <div>
//                                             <h6 className="mb-0 fw-bold">Business & Legal</h6>
//                                             <small className="text-muted">GST, FSSAI, units, and image</small>
//                                         </div>
//                                     </div>
//                                 </Accordion.Header>
//                                 <Accordion.Body className="pt-3">
//                                     <Row className="g-2 mb-3">
//                                         <Col md={3}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">GSTIN <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Control size="sm" type="text" placeholder="22AAAAA0000A1Z5" name="gstin" value={inputData.gstin} onChange={handleChange} required />
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={3}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">FSSAI</Form.Label>
//                                                 <Form.Control size="sm" type="text" placeholder="FSSAI license number" name="fssai" value={inputData.fssai} onChange={handleChange} />
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={6}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Outlet Image <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Control id="imageInput" size="sm" accept="image/*" type="file" onChange={handleImageChange} required />
//                                                 <Form.Text className="text-muted small">1410x1100 pixels recommended</Form.Text>
//                                             </Form.Group>
//                                         </Col>
//                                     </Row>

//                                     <Row className="g-2">
//                                         <Col md={4}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Food Unit</Form.Label>
//                                                 <Select options={foodUnitOptions} value={foodUnitOptions?.find(o => o.value === inputData.foodUnitId)} onChange={(sel) => handleUnitChange('foodUnitId', sel)} placeholder="Select Food Unit" isSearchable components={animatedComponents} styles={customSelectStyles} isClearable />
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={4}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Beverage Unit</Form.Label>
//                                                 <Select options={beverageUnitOptions} value={beverageUnitOptions?.find(o => o.value === inputData.beverageUnitId)} onChange={(sel) => handleUnitChange('beverageUnitId', sel)} placeholder="Select Beverage Unit" isSearchable components={animatedComponents} styles={customSelectStyles} isClearable />
//                                             </Form.Group>
//                                         </Col>
//                                         <Col md={4}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Central Unit</Form.Label>
//                                                 <Select options={centralUnitOptions} value={centralUnitOptions?.find(o => o.value === inputData.centralUnitId)} onChange={(sel) => handleUnitChange('centralUnitId', sel)} placeholder="Select Central Unit" isSearchable styles={customSelectStyles} components={animatedComponents} isClearable />
//                                             </Form.Group>
//                                         </Col>
//                                     </Row>
//                                 </Accordion.Body>
//                             </Accordion.Item>

//                             {/* Section 4: Payment & Platforms */}
//                             <Accordion.Item eventKey="3" className="border-0 mb-3 shadow-sm">
//                                 <Accordion.Header>
//                                     <div className="d-flex align-items-center">
//                                         <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
//                                             <i className="ri-bank-card-line text-primary"></i>
//                                         </div>
//                                         <div>
//                                             <h6 className="mb-0 fw-bold">Payment & Platforms</h6>
//                                             <small className="text-muted">Payment method and delivery platforms</small>
//                                         </div>
//                                     </div>
//                                 </Accordion.Header>
//                                 <Accordion.Body className="pt-3">
//                                     <Row className="g-2 mb-3">
//                                         <Col md={4}>
//                                             <Form.Group>
//                                                 <Form.Label className="small fw-semibold">Payment Method <span className="text-danger">*</span></Form.Label>
//                                                 <Form.Select size="sm" name="paymentType" value={inputData.paymentType} onChange={handleChange} required>
//                                                     <option value="">Select method</option>
//                                                     <option value="PAYMENT_GATEWAY">Razor Pay</option>
//                                                     <option value="CARD_SWIPE">Pine Labs Machine</option>
//                                                 </Form.Select>
//                                             </Form.Group>
//                                         </Col>
//                                     </Row>

//                                     {inputData.paymentType === "CARD_SWIPE" && (
//                                         <Card className="bg-light border-0 mb-3">
//                                             <Card.Body className="p-3">
//                                                 <h6 className="small fw-bold mb-2">Pine Labs Credentials</h6>
//                                                 <Row className="g-2">
//                                                     <Col md={3}>
//                                                         <Form.Control size="sm" type="text" placeholder="Merchant ID" name="pineLabCredentials.merchantId" value={inputData?.pineLabCredentials?.merchantId} onChange={handleChange} required />
//                                                     </Col>
//                                                     <Col md={3}>
//                                                         <Form.Control size="sm" type="text" placeholder="Security Token" name="pineLabCredentials.securityToken" value={inputData?.pineLabCredentials?.securityToken} onChange={handleChange} required />
//                                                     </Col>
//                                                     <Col md={3}>
//                                                         <Form.Control size="sm" type="text" placeholder="Client ID" name="pineLabCredentials.clientId" value={inputData?.pineLabCredentials?.clientId} onChange={handleChange} required />
//                                                     </Col>
//                                                     <Col md={3}>
//                                                         <Form.Control size="sm" type="text" placeholder="Store ID" name="pineLabCredentials.storeId" value={inputData?.pineLabCredentials?.storeId} onChange={handleChange} required />
//                                                     </Col>
//                                                 </Row>
//                                             </Card.Body>
//                                         </Card>
//                                     )}

//                                     <div className="d-flex justify-content-between align-items-center mb-2">
//                                         <h6 className="small fw-bold mb-0">Delivery Platforms</h6>
//                                         <Button size="sm" variant="outline-primary" onClick={handleAddItem}>
//                                             <i className="ri-add-line me-1"></i>Add Platform
//                                         </Button>
//                                     </div>

//                                     {inputData?.platForms?.map((item, index) => (
//                                         <Card key={index} className="bg-light border-0 mb-2">
//                                             <Card.Body className="p-2">
//                                                 <Row className="g-2 align-items-center">
//                                                     <Col md={5}>
//                                                         <Form.Control size="sm" type="text" placeholder="Platform name (e.g., Zomato)" name="name" value={item.name} onChange={(e) => handleChange(e, index)} />
//                                                     </Col>
//                                                     <Col md={5}>
//                                                         <Form.Control size="sm" type="text" placeholder="https://example.com" name="webLink" value={item.webLink} onChange={(e) => handleChange(e, index)} />
//                                                     </Col>
//                                                     <Col md={2}>
//                                                         {inputData.platForms.length > 1 && (
//                                                             <Button size="sm" variant="outline-danger" onClick={() => handleRemoveItem(index)} className="w-100">
//                                                                 <i className="ri-delete-bin-line"></i>
//                                                             </Button>
//                                                         )}
//                                                     </Col>
//                                                 </Row>
//                                             </Card.Body>
//                                         </Card>
//                                     ))}
//                                 </Accordion.Body>
//                             </Accordion.Item>
//                         </Accordion>

//                         {/* Form Actions */}
//                         <Card className="border-0 shadow-sm mt-3">
//                             <Card.Body className="py-3">
//                                 <Row className="align-items-center">
//                                     <Col>
//                                         <div className="d-flex align-items-center">
//                                             <i className="ri-information-line text-primary me-2"></i>
//                                             <small className="text-muted">
//                                                 Fields marked with <span className="text-danger">*</span> are required
//                                             </small>
//                                         </div>
//                                     </Col>
//                                     <Col className="text-end">
//                                         <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => navigate('/outlet-management/outlets')}>
//                                             Cancel
//                                         </Button>
//                                         <Button type="submit" variant="primary" size="sm" disabled={isLoading || !isFormValid()} className="px-4">
//                                             {isLoading ? (
//                                                 <><span className="spinner-border spinner-border-sm me-2" />Creating...</>
//                                             ) : (
//                                                 'Create Outlet'
//                                             )}
//                                         </Button>
//                                     </Col>
//                                 </Row>
//                             </Card.Body>
//                         </Card>
//                     </Form>
//                 </div>
//                 <LocationPickerModal
//                     show={showMapModal}
//                     onHide={() => setShowMapModal(false)}
//                     onConfirm={handleLocationConfirm}
//                     initialLat={inputData.latitude}
//                     initialLng={inputData.longitude}
//                 />
//             </div>
//         </React.Fragment>
//     );
// };

// export default AddOutlet;